import Webcam from "react-webcam";

import { useRef, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import MDBox from "theme/components/MDBox";
import MDButton from "theme/components/MDButton";
import MDTypography from "theme/components/MDTypography";

import {
  Grid,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton,
  Box,
  ListItem,
  ListItemText,
} from "@mui/material";

function ImageField({ formRef, draftIndex, displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const webcamRef = useRef(`${fieldRef}_${displayName}`);
  const [imgSrc, setImgSrc] = useState(null);
  const [capturedImg, setCapturedImg] = useState(null);
  const [cameraUsed, setCameraUsed] = useState(false);

  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  const [openWebCamDialog, setOpenWebCamDialog] = useState(false);

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setErrorMsg("No photo image found.");
      showFieldError(true);
      return;
    }

    setImgSrc(imageSrc);
    setCapturedImg(imageSrc);
    setOpenWebCamDialog(false);
  }, [webcamRef]);

  const [hasSavedValue, setHasSavedValue] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!formRef) return;

    const saved = localStorage.getItem(`draft_${formRef}`);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      const val = parsed?.[draftIndex].values?.[fieldRef];

      if (val !== undefined && val !== null && val !== "") {
        setDraftValue(val);
        setHasSavedValue(true);
      }
    } catch (e) {
      console.error("Draft parse error", e);
    }
  }, [formRef, fieldRef]);

  return (
    <MDBox>
      {hasSavedValue ? (
        <MDBox>
          <ListItem divider>
            <MDBox mr={1}>
              <Icon color="success">check_circle</Icon>
            </MDBox>
            <ListItemText
              primary={
                <MDTypography variant="h6" fontSize={14} fontWeight="regular">
                  {displayName}
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              color="warning"
              variant="outlined"
              iconOnly
              onClick={() => {
                try {
                  const key = `draft_${formRef}`;
                  const saved = localStorage.getItem(key);
                  if (!saved) return;

                  const parsed = JSON.parse(saved);

                  if (parsed?.values && fieldRef in parsed.values) {
                    delete parsed.values[fieldRef];
                  }

                  // persist updated draft
                  localStorage.setItem(key, JSON.stringify(parsed));

                  // update UI state
                  setHasSavedValue(false);
                  setDraftValue(null);
                } catch (e) {
                  console.error("Failed to clear draft field", e);
                }
              }}
            >
              <Icon>edit</Icon>
            </MDButton>
          </ListItem>
          <MDBox>
            <img
              src={draftValue}
              alt="captured"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </MDBox>
          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={draftValue}
          />
        </MDBox>
      ) : (
        <MDBox>
          {/* ERROR */}
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={5000}
            onClose={() => showFieldError(false)}
            open={fieldError}
          >
            <Alert severity="error" sx={{ width: "100%" }}>
              {errorMsg}
            </Alert>
          </Snackbar>

          {/* DIALOG (UNCHANGED) */}
          <Dialog open={openWebCamDialog}>
            <DialogTitle>
              <MDBox>{displayName}</MDBox>
            </DialogTitle>
            <DialogContent>
              <MDBox mt={2} mb={3}>
                <Webcam
                  width="100%"
                  ref={webcamRef}
                  screenshotQuality={1}
                  videoConstraints={videoConstraints}
                  mirrored={cameraUsed}
                />
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => setOpenWebCamDialog(false)}
                color="dark"
                iconOnly
                circular
                variant="outlined"
              >
                <Icon>close</Icon>
              </MDButton>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <MDButton
                onClick={() => setCameraUsed(!cameraUsed)}
                color="warning"
                iconOnly
                circular
                variant="outlined"
              >
                <Icon>cameraswitch</Icon>
              </MDButton>
              &nbsp;
              <MDButton
                color="info"
                variant="outlined"
                iconOnly
                circular
                onClick={capture}
              >
                <Icon>photo_camera</Icon>
              </MDButton>
            </DialogActions>
          </Dialog>

          {/* HEADER (UNCHANGED) */}
          <MDBox sx={{ minWidth: 120, width: "100%" }}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <MDTypography
                  variant="h6"
                  fontWeight="light"
                  fontSize="small"
                  mb={2}
                >
                  {displayName}
                </MDTypography>
              </Grid>

              <Grid item xs={6}>
                <MDBox align="right">
                  <MDButton
                    color="dark"
                    variant="contained"
                    iconOnly
                    size="small"
                    onClick={() => setOpenWebCamDialog(true)}
                  >
                    <Icon>camera</Icon>
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>

            {/* ✅ CAPTURE BOX */}
            <Box
              sx={{
                border: "2px dashed #d2d6da",
                borderRadius: "12px",
                height: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                backgroundColor: "#f8f9fa",
                mt: 2,
              }}
            >
              {!imgSrc && (
                <Box textAlign="center">
                  <IconButton
                    color="primary"
                    onClick={() => setOpenWebCamDialog(true)}
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundColor: "#e3f2fd",
                    }}
                  >
                    <Icon fontSize="large">photo_camera</Icon>
                  </IconButton>

                  <MDTypography variant="caption" mt={1} display="block">
                    Tap to take photo
                  </MDTypography>
                </Box>
              )}

              {imgSrc && (
                <>
                  <img
                    src={imgSrc}
                    alt="captured"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  {/* EXACT CLEAR BUTTON */}
                  <IconButton
                    onClick={() => {
                      setImgSrc(null);
                      setCapturedImg(null);
                    }}
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      backgroundColor: "white",
                      border: "1px solid rgba(0,0,0,0.2)",
                      boxShadow: 3,
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Icon color="error">close</Icon>
                  </IconButton>
                </>
              )}
            </Box>
          </MDBox>

          <input
            type="hidden"
            id={`dff_${fieldRef}`}
            name={fieldRef}
            value={capturedImg}
          />
        </MDBox>
      )}
    </MDBox>
  );
}

ImageField.propTypes = {
  formRef: PropTypes.string.isRequired,
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

ImageField.defaultProps = {
  options: null,
};

export default ImageField;
